import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const AmortizationCalculatorPage = () => {
  const [loanAmount, setLoanAmount] = useState('200000');
  const [interestRate, setInterestRate] = useState('5');
  const [loanTerm, setLoanTerm] = useState('30');
  const [termUnit, setTermUnit] = useState('years');
  const [result, setResult] = useState(null);
  const [scheduleView, setScheduleView] = useState('yearly');

  const calculate = () => {
    const P = parseFloat(loanAmount) || 0;
    const r = (parseFloat(interestRate) || 0) / 100 / 12;
    const n = termUnit === 'years' ? parseFloat(loanTerm) * 12 : parseFloat(loanTerm);

    const M = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const total = M * n;
    const interest = total - P;

    const schedule = [];
    let balance = P;
    for (let i = 1; i <= n; i++) {
      const interestPmt = balance * r;
      const principalPmt = M - interestPmt;
      balance = Math.max(0, balance - principalPmt);
      schedule.push({ month: i, year: Math.ceil(i / 12), interest: interestPmt, principal: principalPmt, balance });
    }

    const yearly = [];
    const totalYears = Math.ceil(n / 12);
    for (let y = 1; y <= totalYears; y++) {
      const yData = schedule.filter(s => s.year === y);
      yearly.push({
        year: y,
        interest: yData.reduce((sum, s) => sum + s.interest, 0),
        principal: yData.reduce((sum, s) => sum + s.principal, 0),
        balance: yData[yData.length - 1]?.balance || 0
      });
    }

    setResult({ payment: M, total, interest, principal: P, months: n, schedule, yearly });
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link><span className="mx-2">›</span>
            <span className="text-gray-400">Amortization Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-4">Amortization Calculator</h1>
          <p className="text-gray-400 mb-6">Generate a full amortization schedule showing how your loan payments are split between principal and interest.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-28">Loan Amount</label>
                <div className="flex items-center gap-1"><span className="text-gray-400">$</span>
                  <Input type="number" value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)}
                    className="w-36 bg-[#0d0d0d] border-[#2a2a2a] text-white" /></div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-28">Interest Rate</label>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.01" value={interestRate} onChange={(e) => setInterestRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" /><span className="text-gray-400">%</span></div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-28">Loan Term</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={loanTerm} onChange={(e) => setLoanTerm(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={termUnit} onChange={(e) => setTermUnit(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="years">years</option><option value="months">months</option></select>
                </div>
              </div>
            </div>
            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <>
                <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="text-center"><p className="text-gray-400 text-sm">Monthly Payment</p><p className="text-green-400 font-bold text-xl">{formatCurrency(result.payment)}</p></div>
                    <div className="text-center"><p className="text-gray-400 text-sm">Total Payments</p><p className="text-white font-bold text-xl">{formatCurrency(result.total)}</p></div>
                    <div className="text-center"><p className="text-gray-400 text-sm">Total Interest</p><p className="text-cyan-400 font-bold text-xl">{formatCurrency(result.interest)}</p></div>
                    <div className="text-center"><p className="text-gray-400 text-sm"># of Payments</p><p className="text-white font-bold text-xl">{result.months}</p></div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white">Amortization Schedule</h2>
                    <div className="flex gap-2">
                      <button onClick={() => setScheduleView('yearly')} className={`px-4 py-2 rounded text-sm ${scheduleView === 'yearly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}>Yearly</button>
                      <button onClick={() => setScheduleView('monthly')} className={`px-4 py-2 rounded text-sm ${scheduleView === 'monthly' ? 'bg-cyan-600 text-white' : 'bg-[#2a2a2a] text-gray-400'}`}>Monthly</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-80 overflow-y-auto bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-[#1a1a1a]">
                        <tr className="border-b border-[#2a2a2a]">
                          <th className="text-left py-2 px-3 text-gray-400">{scheduleView === 'yearly' ? 'Year' : 'Month'}</th>
                          <th className="text-right py-2 px-3 text-gray-400">Interest</th>
                          <th className="text-right py-2 px-3 text-gray-400">Principal</th>
                          <th className="text-right py-2 px-3 text-gray-400">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(scheduleView === 'yearly' ? result.yearly : result.schedule.slice(0, 60)).map((row, i) => (
                          <tr key={i} className="border-b border-[#2a2a2a]/50 hover:bg-[#1a1a1a]">
                            <td className="py-2 px-3 text-white">{scheduleView === 'yearly' ? row.year : row.month}</td>
                            <td className="py-2 px-3 text-right text-gray-300">{formatCurrency(row.interest)}</td>
                            <td className="py-2 px-3 text-right text-gray-300">{formatCurrency(row.principal)}</td>
                            <td className="py-2 px-3 text-right text-white font-medium">{formatCurrency(row.balance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
          <AdInContent />
          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Mortgage', path: '/mortgage-calculator' }, { name: 'Loan', path: '/loan-calculator' }, { name: 'Payment', path: '/payment-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AmortizationCalculatorPage;
